import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { accountQuery, cmsPageQuery, hostBaseUrl } from '@/api';
import { useTranslations } from '@/i18n/TranslationsProvider';
import { EmptyState, Spinner, Toast } from '@/ui/common';
import { CmsPagePanel } from '@/ui/cms';

/** CMS-backed content pages, matching the legacy `/page/{slug}` module. */
export const Route = createFileRoute('/page/$slug')({
  component: CmsContentPage,
});

function CmsContentPage() {
  const { t, i18n } = useTranslation();
  const { selectedLanguage } = useTranslations();
  const { slug } = Route.useParams();
  const isTranslationReady =
    selectedLanguage === 'en' || i18n.hasResourceBundle(selectedLanguage, 'translation');
  const label = (key: string, fallback: string) =>
    isTranslationReady ? t(key, { defaultValue: fallback }) : fallback;

  const { data: account } = useQuery(accountQuery());
  const accountGuid = account?.Guid ?? '';

  const {
    data: page,
    isLoading,
    isError,
    error,
  } = useQuery(cmsPageQuery(accountGuid, slug, selectedLanguage));

  return (
    <div className="flex w-full flex-col px-4 pt-4 pb-6 lg:px-[200px] lg:pt-8 lg:pb-12">
      {isError ? (
        <Toast tone="danger">
          {label('Could not load page', 'Could not load page')}
          {error instanceof Error && error.message ? `: ${error.message}` : ''}
        </Toast>
      ) : null}

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner label={label('Loading page', 'Loading page')} />
        </div>
      ) : page ? (
        <CmsPagePanel
          page={page}
          usersBaseUrl={hostBaseUrl('users')}
          accountGuid={accountGuid}
          label={label}
        />
      ) : !isError ? (
        <EmptyState
          icon="book"
          title={label('Page not found', 'Page not found')}
          description={label(
            'This page may have been removed.',
            'This page may have been removed.',
          )}
        />
      ) : null}
    </div>
  );
}
